<template>
  <SourceLinkBanner
    v-if="actsAsSupplier"
    :message="$t('Supplier billing')"
    :subtitle="$t('These are payment requests sent to your clients, created from approved supplier timesheets.')"
    :link-url="fmsBillsUrl"
    :link-text="$t('View received requests')"
  />
  <PaymentRequestTable>
    <template #title="{ accountInfo }">
      <PageHeader>
        <template #header-title>
          {{ $t('Payment Requests') }}
        </template>
        <template v-if="!loadingHires" #header-actions>
          <TimesheetImportButton v-if="timesheetImportEnabled" />
          <WdsButton
            v-if="hasOneActiveHire"
            data-test="payment-requests-request-payment-button"
            primary
            :href="url"
          >
            {{ $t('Request payment') }}
          </WdsButton>
          <WdsButton
            v-else
            primary
            data-test="payment-requests-request-payment-button"
            @click="toggleModalJobSelection"
          >
            {{ $t('Request payment') }}
          </WdsButton>
        </template>
        <template #header-metadata>
          <WdsStack class="tw-items-center tw-space-x-2 tw-mb-5">
            <WdsIcon name="finance" :size="16" color="#9999AA" />
            <span>{{ $t('Bank details') }}</span>
            <a href="/profile/edit#bank-setup">
              <div
                v-if="accountInfo === null"
                class="skeleton !tw-bg-gray-50 tw-min-w-[120px] tw-min-h-[20px] tw-rounded"
              ></div>
              <WdsLabel v-else subtle sm :gray="accountInfo" :warning="!accountInfo">
                <WdsStack items-center class="tw-items-center tw-space-x-1">
                  {{ accountInfo || $t('Update missing information') }}
                  <WdsIcon name="edit" :size="12" class="tw-ml-1.5" />
                </WdsStack>
              </WdsLabel>
            </a>
          </WdsStack>
        </template>
      </PageHeader>
    </template>
  </PaymentRequestTable>
  <ModalSelectJobForPaymentRequest
    :is-open="isModalJobSelectionOpen"
    :show-worker-name="viewerIsStaffingAgency"
    @cancel="isModalJobSelectionOpen = false"
  />
</template>

<script setup lang="ts">
import { WdsButton, WdsIcon, WdsLabel, WdsStack } from '@worksome/wds-components'
import { ref, provide, computed } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import PaymentRequestTable from '@/components/RequestPaymentDashboard/PaymentRequestsTable.vue'
import { useTranslation } from '@/composables/translation'
import ModalSelectJobForPaymentRequest from '@/components/RequestPaymentDashboard/ModalSelectJobForPaymentRequest.vue'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import PageHeader from '@/components/PageHeader.vue'
import SourceLinkBanner from '@/components/SourceLinkBanner.vue'
import { useAccountCapabilities } from '@/composables/useAccountCapabilities'
import TimesheetImportButton from '@/components/TimesheetImport/TimesheetImportButton.vue'

const { actsAsSupplier } = useAccountCapabilities()

const props = withDefaults(
  defineProps<{
    viewerIsStaffingAgency?: boolean
    timesheetImportEnabled?: boolean
  }>(),
  {
    viewerIsStaffingAgency: false,
    timesheetImportEnabled: false,
  },
)

const { $t } = useTranslation()

const isModalJobSelectionOpen = ref(false)
const toggleModalJobSelection = () => {
  isModalJobSelectionOpen.value = !isModalJobSelectionOpen.value
}

const { result: activeHires, loading: loadingHires } = useQuery(
  gql`
    query hires($accounts: [ID!]) {
      hires(first: 100000, accounts: $accounts, eligibleForPaymentRequests: true) {
        data {
          id
          job {
            completed
          }
          conversation {
            closedAt
          }
        }
      }
    }
  `,
  {
    accounts: [currentAccount.globalId],
  },
  {
    clientId: 'publicApi',
  },
)

const hires = computed(
  () =>
    activeHires.value?.hires?.data?.filter(
      (hire) => !(hire.conversation?.closedAt || hire.job.completed),
    ) ?? [],
)

const hasOneActiveHire = computed(() => hires.value.length === 1)

const url = computed(() => {
  if (!hasOneActiveHire.value) return null

  return laravelNamedRoute(namedRoutes.bills.create, { hire: hires.value[0].id })
})

const fmsBillsUrl = '/billing/fms-bills'

provide('viewerIsStaffingAgency', props.viewerIsStaffingAgency)
</script>
